import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatError } from "@/lib/api";
import { AUTH } from "@/constants/testIds";
import { ShieldCheck } from "lucide-react";
import ThemeToggle from "@/components/ThemeToggle";
import PasswordInput from "@/components/PasswordInput";
import BuildBadge from "@/components/BuildBadge";
import AppStatusDot from "@/components/AppStatusDot";
import MadeBySSPL from "@/components/MadeBySSPL";

export default function Login() {
  const { login, verifyMfa, mfaPending } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from?.pathname || "/app/rooms";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const res = await login(email, password);
      if (!res.mfa_required) navigate(from, { replace: true });
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  const submitMfa = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await verifyMfa(code);
      navigate(from, { replace: true });
    } catch (e) {
      setErr(formatError(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <ThemeToggle />
      <div className="hidden md:flex flex-col justify-between relative bg-card border-r border-border p-12 overflow-hidden">
        <div className="absolute inset-0 hero-gradient pointer-events-none" />
        <div className="absolute inset-0 sovereign-grid opacity-30 pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-md bg-primary flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-display text-base font-medium">Sovereign VDR</span>
          </div>
        </div>
        <div className="relative">
          <div className="label-mono text-muted-foreground">— A sovereign vault</div>
          <h2 className="mt-4 font-display text-4xl tracking-tighter font-medium leading-tight">
            Boardroom-grade due diligence,<br/>held in Australia.
          </h2>
          <div className="mt-10 grid grid-cols-2 gap-px bg-border">
            {[
              ["MFA", "Time-based one-time codes"],
              ["FENCE", "Watermarked viewers"],
              ["AUDIT", "Page-by-page analytics"],
              ["AU", "Sovereign data residency"],
            ].map(([k, v]) => (
              <div key={k} className="bg-card p-5">
                <div className="font-display text-2xl">{k}</div>
                <div className="label-mono text-muted-foreground mt-1">{v}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="label-mono text-muted-foreground">— Back to overview</Link>
          {!mfaPending ? (
            <>
              <h1 className="mt-6 font-display text-3xl font-medium tracking-tight">Sign in</h1>
              <p className="mt-2 text-sm text-muted-foreground">Access your data rooms.</p>
              <form onSubmit={submit} className="mt-8 space-y-4">
                <div>
                  <label className="label-mono">Email</label>
                  <input
                    data-testid={AUTH.loginEmail}
                    type="email" required
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full px-3 py-2.5 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between">
                    <label className="label-mono">Password</label>
                    <Link
                      data-testid={AUTH.loginForgot}
                      to="/forgot-password"
                      className="label-mono text-muted-foreground hover:text-foreground"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <PasswordInput
                    testId={AUTH.loginPassword}
                    required
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="mt-1"
                  />
                </div>
                {err && <div className="text-sm text-destructive">{err}</div>}
                <button
                  data-testid={AUTH.loginSubmit}
                  type="submit" disabled={loading}
                  className="w-full px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
                >
                  {loading ? "Verifying…" : "Sign in"}
                </button>
              </form>
              <div className="mt-6 text-sm text-muted-foreground">
                No account?{" "}
                <Link to="/register" className="text-primary hover:underline">Request access</Link>
              </div>
            </>
          ) : (
            <>
              <h1 className="mt-6 font-display text-3xl font-medium tracking-tight">Multi-factor</h1>
              <p className="mt-2 text-sm text-muted-foreground">Enter the 6-digit code from your authenticator app.</p>
              <form onSubmit={submitMfa} className="mt-8 space-y-4">
                <input
                  data-testid={AUTH.mfaCode}
                  type="text" required
                  inputMode="numeric" pattern="[0-9]{6}"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                  className="w-full px-3 py-3 bg-background border border-border rounded-md font-mono text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-ring"
                />
                {err && <div className="text-sm text-destructive">{err}</div>}
                <button
                  data-testid={AUTH.mfaVerify}
                  type="submit" disabled={loading}
                  className="w-full px-4 py-2.5 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-50"
                >
                  {loading ? "Verifying…" : "Verify"}
                </button>
              </form>
            </>
          )}
        </div>
        <div className="absolute bottom-6 inset-x-0 px-8 flex flex-col items-center gap-1.5">
          <AppStatusDot />
          <BuildBadge align="center" />
          <MadeBySSPL align="center" />
        </div>
      </div>
    </div>
  );
}
